<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, shallowRef, watch } from 'vue';
import { RouterLink, type RouteLocationRaw } from 'vue-router';
import UiButton from '@krafta/ui-kit/components/UiButton.vue';
import UiCallout from '@krafta/ui-kit/components/UiCallout.vue';
import type { HelpTour, HelpTourContinuation, HelpTourStepLink } from '../types';

const props = defineProps<{
  tour: HelpTour | null;
  continuation?: HelpTourContinuation | null;
}>();

const emit = defineEmits<{
  finish: [tour: HelpTour];
  skip: [tour: HelpTour];
  continue: [tour: HelpTour, continuation: HelpTourContinuation];
}>();

const open = defineModel<boolean>({ required: true });
const stepIndex = shallowRef(0);
const targetRect = shallowRef<DOMRect | null>(null);
const fallbackMode = shallowRef(false);

const currentStep = computed(() => props.tour?.steps[stepIndex.value] ?? null);
const isLastStep = computed(() => Boolean(props.tour && stepIndex.value >= props.tour.steps.length - 1));
const resolvedContinuation = computed(() => props.continuation ?? props.tour?.continueTo ?? null);
const nextButtonLabel = computed(() => {
  if (!isLastStep.value) return 'Дальше';
  if (resolvedContinuation.value) return `Продолжить: ${resolvedContinuation.value.label}`;
  return 'Завершить';
});

const resolveStepLink = (link: HelpTourStepLink): RouteLocationRaw => {
  if (link.route) return link.route;
  return { name: 'help-page', params: { key: link.helpKey ?? 'help.center' } };
};

const escapeHelpId = (value: string) => {
  const css = globalThis.CSS as { escape?: (input: string) => string } | undefined;
  return css?.escape ? css.escape(value) : value.replace(/["\\]/g, '\\$&');
};

const findTarget = () => {
  const targetHelpId = currentStep.value?.targetHelpId;
  if (!targetHelpId || typeof document === 'undefined') return null;
  return document.querySelector<HTMLElement>(`[data-help-id="${escapeHelpId(targetHelpId)}"]`);
};

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const refreshTargetRect = () => {
  const target = findTarget();
  targetRect.value = target?.getBoundingClientRect() ?? null;
  return target;
};

const prepareStep = async () => {
  fallbackMode.value = false;
  targetRect.value = null;
  await nextTick();

  let target: HTMLElement | null = null;
  for (let attempt = 0; attempt < 5; attempt += 1) {
    target = refreshTargetRect();
    if (target) break;
    await wait(80);
  }

  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    await wait(180);
    refreshTargetRect();
    return;
  }

  if (currentStep.value?.optional) {
    console.warn(`Help tour optional target is missing: ${currentStep.value.targetHelpId}`);
    nextStep();
    return;
  }

  if (currentStep.value) {
    console.warn(`Help tour required target is missing: ${currentStep.value.targetHelpId}`);
  }
  fallbackMode.value = true;
};

const close = () => {
  open.value = false;
};

const finish = () => {
  if (props.tour) {
    emit('finish', props.tour);
  }
  close();
};

const skip = () => {
  if (props.tour) {
    emit('skip', props.tour);
  }
  close();
};

const continueTour = () => {
  const continuation = resolvedContinuation.value;
  if (props.tour && continuation) {
    emit('continue', props.tour, continuation);
  }
  close();
};

const nextStep = () => {
  if (!props.tour) return;
  if (isLastStep.value) {
    finish();
    return;
  }
  stepIndex.value += 1;
  void prepareStep();
};

const previousStep = () => {
  if (stepIndex.value === 0) return;
  stepIndex.value -= 1;
  void prepareStep();
};

const onKeydown = (event: KeyboardEvent) => {
  if (!open.value) return;
  if (event.key === 'Escape') {
    skip();
  }
  if (event.key === 'ArrowRight') {
    nextStep();
  }
  if (event.key === 'ArrowLeft') {
    previousStep();
  }
};

const onViewportChange = () => {
  if (open.value && !fallbackMode.value) {
    refreshTargetRect();
  }
};

const bindListeners = () => {
  document.addEventListener('keydown', onKeydown);
  window.addEventListener('resize', onViewportChange);
  window.addEventListener('scroll', onViewportChange, true);
};

const unbindListeners = () => {
  document.removeEventListener('keydown', onKeydown);
  window.removeEventListener('resize', onViewportChange);
  window.removeEventListener('scroll', onViewportChange, true);
};

watch(
  open,
  (isOpen) => {
    unbindListeners();
    if (isOpen) {
      stepIndex.value = 0;
      bindListeners();
      void prepareStep();
    }
  },
  { immediate: true }
);

watch(
  () => props.tour?.key,
  () => {
    if (open.value) {
      stepIndex.value = 0;
      void prepareStep();
    }
  }
);

onBeforeUnmount(unbindListeners);

const spotlightStyle = computed(() => {
  const rect = targetRect.value;
  if (!rect) return {};
  const padding = 8;
  return {
    top: `${Math.max(0, rect.top - padding)}px`,
    left: `${Math.max(0, rect.left - padding)}px`,
    width: `${rect.width + padding * 2}px`,
    height: `${rect.height + padding * 2}px`
  };
});

const cardStyle = computed(() => {
  const rect = targetRect.value;
  const placement = currentStep.value?.placement ?? 'auto';
  if (!rect || fallbackMode.value) return {};

  const margin = 16;
  const cardWidth = 360;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const resolvedPlacement =
    placement === 'auto'
      ? rect.right + cardWidth + margin < viewportWidth
        ? 'right'
        : 'bottom'
      : placement;

  if (resolvedPlacement === 'right') {
    return {
      top: `${Math.min(Math.max(margin, rect.top), viewportHeight - 260)}px`,
      left: `${Math.min(rect.right + margin, viewportWidth - cardWidth - margin)}px`
    };
  }

  if (resolvedPlacement === 'left') {
    return {
      top: `${Math.min(Math.max(margin, rect.top), viewportHeight - 260)}px`,
      left: `${Math.max(margin, rect.left - cardWidth - margin)}px`
    };
  }

  if (resolvedPlacement === 'top') {
    return {
      top: `${Math.max(margin, rect.top - 260)}px`,
      left: `${Math.min(Math.max(margin, rect.left), viewportWidth - cardWidth - margin)}px`
    };
  }

  return {
    top: `${Math.min(rect.bottom + margin, viewportHeight - 260)}px`,
    left: `${Math.min(Math.max(margin, rect.left), viewportWidth - cardWidth - margin)}px`
  };
});
</script>

<template>
  <Teleport to="body">
    <Transition name="help-tour-fade">
      <div
        v-if="open && tour && currentStep"
        class="help-tour"
        :class="{ 'help-tour--dim': fallbackMode || !targetRect }"
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-tour-title"
      >
        <div
          v-if="targetRect && !fallbackMode"
          class="help-tour__spotlight"
          :style="spotlightStyle"
          aria-hidden="true"
        />

        <section
          class="help-tour__card"
          :class="{ 'help-tour__card--center': fallbackMode || !targetRect }"
          :style="cardStyle"
        >
          <p class="help-tour__progress">
            Шаг {{ stepIndex + 1 }} из {{ tour.steps.length }}
          </p>
          <h2 id="help-tour-title">
            {{ currentStep.title }}
          </h2>
          <p>{{ currentStep.body }}</p>

          <div
            v-if="currentStep.links?.length"
            class="help-tour__links"
          >
            <span>Связанные переходы</span>
            <RouterLink
              v-for="link in currentStep.links"
              :key="`${link.label}:${link.route ?? link.helpKey}`"
              class="help-tour__link"
              :to="resolveStepLink(link)"
              @click="close"
            >
              <strong>{{ link.label }}</strong>
              <small v-if="link.description">{{ link.description }}</small>
            </RouterLink>
          </div>

          <UiCallout
            v-if="fallbackMode"
            tone="warning"
            compact
          >
            Элемент «{{ currentStep.targetHelpId }}» сейчас не найден на экране. Возможно, он скрыт ролью,
            загрузкой данных или адаптивной раскладкой.
          </UiCallout>

          <div class="help-tour__actions">
            <UiButton
              variant="ghost"
              type="button"
              @click="skip"
            >
              Пропустить
            </UiButton>
            <UiButton
              variant="secondary"
              type="button"
              :disabled="stepIndex === 0"
              @click="previousStep"
            >
              Назад
            </UiButton>
            <UiButton
              v-if="isLastStep && resolvedContinuation"
              variant="secondary"
              type="button"
              @click="finish"
            >
              Завершить
            </UiButton>
            <UiButton
              type="button"
              @click="isLastStep && resolvedContinuation ? continueTour() : nextStep()"
            >
              {{ nextButtonLabel }}
            </UiButton>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.help-tour {
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: transparent;
}

.help-tour--dim {
  background: rgba(2, 6, 23, 0.4);
}

.help-tour__spotlight {
  position: fixed;
  border: 2px solid var(--ui-accent);
  border-radius: var(--ui-radius-xl);
  box-shadow:
    0 0 0 9999px rgba(2, 6, 23, 0.42),
    0 0 0 6px color-mix(in srgb, var(--ui-accent) 30%, transparent),
    0 0 22px 4px color-mix(in srgb, var(--ui-accent) 35%, transparent);
  pointer-events: none;
  transition: all 0.16s ease;
}

.help-tour__card {
  position: fixed;
  display: grid;
  width: min(22.5rem, calc(100vw - 2rem));
  gap: var(--ui-space-3);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-2xl);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-popover);
  color: var(--ui-foreground);
  padding: var(--ui-space-5);
}

.help-tour__card--center {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.help-tour__progress,
.help-tour__card h2,
.help-tour__card p {
  margin: 0;
}

.help-tour__progress {
  color: var(--ui-foreground-muted);
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.help-tour__card h2 {
  font-size: 1.2rem;
}

.help-tour__card > p:not(.help-tour__progress) {
  color: var(--ui-foreground-muted);
  line-height: 1.5;
}

.help-tour__links {
  display: grid;
  gap: var(--ui-space-2);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-xl);
  background: var(--ui-surface-muted);
  padding: var(--ui-space-3);
}

.help-tour__links > span {
  color: var(--ui-foreground-muted);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.help-tour__link {
  display: grid;
  gap: 0.15rem;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-control-radius);
  background: var(--ui-surface);
  color: var(--ui-foreground);
  padding: var(--ui-space-2) var(--ui-space-3);
  text-decoration: none;
}

.help-tour__link:hover {
  border-color: var(--ui-accent);
}

.help-tour__link strong {
  font-size: 0.86rem;
}

.help-tour__link small {
  color: var(--ui-foreground-muted);
  line-height: 1.35;
}

.help-tour__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--ui-space-2);
}

.help-tour-fade-enter-active,
.help-tour-fade-leave-active {
  transition: opacity 0.16s ease;
}

.help-tour-fade-enter-from,
.help-tour-fade-leave-to {
  opacity: 0;
}
</style>
